import { Button } from "@mui/material";
import moment from "moment";

// poExtraChargeId (or a charge's own _id) can arrive as a populated object instead
// of a plain id depending on how the reference was hydrated -- compare unwrapped
// ids so a saved received charge is never silently dropped from a match.
const getRefId = (value) =>
  value && typeof value === "object" ? value._id ?? value.id : value;
const refIdsMatch = (a, b) => String(getRefId(a)) === String(getRefId(b));

export const getHeaderConfig = (receivedItems, editRecord) => {
  const headers = [
    {
      label: "Category",
      id: "mainCategory",
      render: (data) => {
        const { lineItem = {} } = data;
        return (lineItem && lineItem.name) || "";
      },
    },
    {
      label: "Subcategory",
      id: "categoryName",
      render: (data) => {
        const { subCategory = {} } = data || {};
        return (subCategory && subCategory.name) || "";
      },
    },
    {
      label: "Item Name",
      id: "name",
      render: (data) => {
        const { item = {} } = data;
        return (item && item.name) || "";
      },
    },
    {
      label: "HSN Code",
      id: "hsnCode",
      render: (data) => {
        let item = receivedItems.find((r) => r.poItemId === data._id) || {};
        return (item && item.hsnCode) || "";
      },
    },
    {
      label: "UOM",
      id: "uom",
    },
    {
      label: "Qty",
      id: "quantity",
    },
    {
      label: "Inwarded Qty",
      id: "inwardedQuantity",
    },
    {
      label: "Received Qty",
      id: "receivedItems",
      render: (data) => {
        let item = receivedItems.find((r) => r.poItemId === data._id) || {};
        return (item && item.receivedQuantity) || "";
      },
    },
    {
      label: "Base Amount",
      id: "receivedAmount",
      render: (data) => {
        let item = receivedItems.find((r) => r.poItemId === data._id) || {};
        return getGRNOrderAmount([data], [item]);
      },
    },
    {
      label: "Tax Amount",
      id: "taxAmount",
      render: (data) => {
        let item = receivedItems.find((r) => r.poItemId === data._id) || {};
        return getGRNOrderTaxAmount([data], [item]);
      },
    },
    {
      label: "Total Amount",
      id: "totalAmount",
      render: (data) => {
        let item = receivedItems.find((r) => r.poItemId === data._id) || {};
        return getGRNOrderAmountWithTax([data], [item]);
      },
    },
    {
      label: "Ticket No.",
      id: "ticketNumber",
    },
    {
      label: "Ticket Creation Date",
      id: "ticketCreationDate",
      render: (data) => {
        return (
          (data?.ticketCreationDate &&
            moment(data.ticketCreationDate).format("DD-MM-YYYY")) ||
          ""
        );
      },
    },
  ];
  return headers;
};

export const getGRNOrderAmount = (items, receivedItems) => {
  let sum = 0;
  items.forEach((item) => {
    let receivedItem = receivedItems.find((r) => r.poItemId === item._id);
    if (receivedItem) {
      let amount = receivedItem.receivedQuantity * item.rate;
      // let taxAmount = (amount * item.taxRate) / 100;
      sum = sum + amount;
    }
  });

  return Math.round(sum).toFixed(2);
};

export const getGRNOrderTaxAmount = (items, receivedItems) => {
  let sum = 0;
  items.forEach((item) => {
    let receivedItem = receivedItems.find((r) => r.poItemId === item._id);
    if (receivedItem) {
      let amount = receivedItem.receivedQuantity * item.rate;
      let taxAmount = (amount * item.taxRate) / 100;
      sum = sum + taxAmount;
    }
  });

  return Math.round(sum).toFixed(2);
};

export const getGRNOrderAmountWithTax = (items, receivedItems) => {
  let sum = 0;
  items?.forEach?.((item) => {
    let receivedItem = receivedItems?.find?.((r) => r.poItemId === item._id);
    if (receivedItem) {
      let amount = receivedItem.receivedQuantity * item.rate;
      let taxAmount = (amount * item.taxRate) / 100;
      sum = sum + amount + taxAmount;
    }
  });

  return Math.round(sum).toFixed(2);
};

// `data` here is a merged row (PO extra-charge fields + its matched received-charge
// fields, already joined by normalized id in grn-details/index.js) -- no closure
// lookup needed, columns just read straight off it.
export const getExtraChargesHeaderConfig = () => {
  const headers = [
    {
      label: "Charge Name",
      id: "chargeName",
      render: (data) =>
        data.chargeCode ? `${data.chargeCode} - ${data.chargeName || ""}` : data.chargeName || "",
    },
    {
      label: "HSN Code",
      id: "hsnCode",
      render: (data) => data.hsnCode || "",
    },
    {
      label: "Rate",
      id: "value",
    },
    {
      label: "Total Qty",
      id: "quantity",
    },
    {
      label: "UOM",
      id: "uom",
    },
    {
      label: "Received Qty",
      id: "receivedQuantity",
      render: (data) => data.receivedQuantity || "",
    },
    {
      label: "Base Amount",
      id: "baseAmount",
      render: (data) => getExtraChargesOrderAmount([data], [data]),
    },
    {
      label: "Tax Amount",
      id: "taxAmount",
      render: (data) => getExtraChargesOrderTaxAmount([data], [data]),
    },
    {
      label: "Total Amount",
      id: "totalAmount",
      render: (data) => getExtraChargesOrderAmountWithTax([data], [data]),
    },
    {
      label: "Comments",
      id: "comments",
      render: (data) => data.comments || "",
    },
  ];
  return headers;
};

export const getExtraChargesOrderAmount = (extraCharges, receivedExtraCharges) => {
  let sum = 0;
  extraCharges.forEach((charge) => {
    let receivedCharge = receivedExtraCharges.find((r) => refIdsMatch(r.poExtraChargeId, charge._id));
    if (receivedCharge) {
      sum = sum + receivedCharge.receivedQuantity * charge.value;
    }
  });

  return Math.round(sum).toFixed(2);
};

export const getExtraChargesOrderTaxAmount = (extraCharges, receivedExtraCharges) => {
  let sum = 0;
  extraCharges.forEach((charge) => {
    let receivedCharge = receivedExtraCharges.find((r) => refIdsMatch(r.poExtraChargeId, charge._id));
    if (receivedCharge) {
      let amount = receivedCharge.receivedQuantity * charge.value;
      sum = sum + (amount * (charge.taxRate || 0)) / 100;
    }
  });

  return Math.round(sum).toFixed(2);
};

export const getExtraChargesOrderAmountWithTax = (extraCharges, receivedExtraCharges) => {
  let sum = 0;
  extraCharges.forEach((charge) => {
    let receivedCharge = receivedExtraCharges.find((r) => refIdsMatch(r.poExtraChargeId, charge._id));
    if (receivedCharge) {
      let amount = receivedCharge.receivedQuantity * charge.value;
      let taxAmount = (amount * (charge.taxRate || 0)) / 100;
      sum = sum + amount + taxAmount;
    }
  });

  return Math.round(sum).toFixed(2);
};

// Full GRN total for a list row: received items (+ their tax) plus received extra
// charges (+ their tax) -- the per-item/per-charge functions above stay untouched
// since they're also used for the individual table columns on this page.
export const getGRNGrandTotal = (grnData = {}) => {
  const { purchaseOrderId = {}, receivedItems = [], receivedExtraCharges = [] } =
    grnData || {};
  const { items = [], extraCharges = [] } = purchaseOrderId || {};
  const itemsTotal = Number(getGRNOrderAmountWithTax(items, receivedItems)) || 0;
  const chargesTotal =
    Number(getExtraChargesOrderAmountWithTax(extraCharges, receivedExtraCharges)) || 0;
  return (itemsTotal + chargesTotal).toFixed(2);
};

export const hasReceivedExtraCharges = (receivedExtraCharges = []) =>
  (receivedExtraCharges || []).some((row) => Number(row?.receivedQuantity) > 0);

export const getGRNExtraChargesTotal = (extraCharges = [], receivedExtraCharges = []) =>
  getExtraChargesOrderAmountWithTax(extraCharges || [], receivedExtraCharges || []);

export const formatGRNExtraCharges = (extraCharges = [], receivedExtraCharges = []) =>
  hasReceivedExtraCharges(receivedExtraCharges)
    ? getGRNExtraChargesTotal(extraCharges, receivedExtraCharges)
    : "-";
